import { take, takeLatest, put, call } from 'redux-saga/effects';
import authSlice from 'store/modules/auth';
import availabilitySlice from 'store/modules/availability';
import settingsSlice from 'store/modules/settings';
import uiSlice from 'store/modules/ui';
import auth from 'api/graphql/auth';
import user from 'api/graphql/user';
import { persistor } from 'store';
import client from 'apollo/client';
import { roles } from 'common/constants/options';
import {
  createSnack,
  saveAuthInfo,
  consumeVoluntaryLogout,
  markVoluntaryLogout,
  removeAuthInfo,
  getUserFriendlyError
} from 'common/utils/general';
import i18n from 'i18n';

/**
 * Generate reset token saga
 *
 * @param {*} action
 */
function* generateResetToken(action) {
  try {
    const { payload: email } = action;
    const {
      data: {
        generatePasswordResetToken: { created, message }
      }
    } = yield auth.generateResetToken(email);

    const errorSnack = createSnack({
      key: 'generate-reset-token-error',
      variant: 'error',
      message
    });

    if (created) {
      yield put(authSlice.actions.generateResetTokenSuccess());
      yield put(authSlice.actions.sendResetToken(email));
    } else {
      yield put(authSlice.actions.generateResetTokenError());
      yield put(uiSlice.actions.enqueueSnackbar(errorSnack));
    }
  } catch (ex) {
    // Un fallo aqui debe ser VISIBLE: despachar Success silenciaba el error (el middleware
    // de errores solo reacciona a acciones *Error) y el usuario quedaba mirando el formulario.
    const message = getUserFriendlyError(ex);
    yield put(authSlice.actions.generateResetTokenError(message));
  }
}

/**
 * Vacía la caché de Apollo. Sin esto, quien inicie sesión después en el mismo navegador ve por un
 * instante las consultas del usuario anterior (`cache-and-network` pinta la caché antes de que
 * llegue la red): datos de OTRA persona en pantalla, así sea un segundo. Nunca bloquea el flujo
 * de sesión: la caché es un acelerador, no la fuente de verdad.
 */
function* clearApolloCache() {
  try {
    yield call([client, client.clearStore]);
  } catch (ex) {
    // Si no se pudo vaciar, la sesión sigue igual; la red trae los datos correctos.
  }
}

/**
 * Login saga
 *
 * @param {*} action
 */
function* login(action) {
  try {
    const response = yield auth.login(action.payload);
    const {
      data: { login: loginResponse }
    } = response;

    const { user: loggedUser } = loginResponse;
    const isTrainer = loggedUser.role === roles.trainer;

    yield call(saveAuthInfo, loginResponse);
    // Puede venir de una sesión caducada que nunca pasó por logout: nada del usuario anterior.
    yield call(clearApolloCache);
    yield put(settingsSlice.actions.find(loggedUser.settings.id));

    yield take(settingsSlice.actions.findSuccess);
    yield put(authSlice.actions.loginSuccess(loginResponse));

    // Bienvenida: se encola DESPUÉS de loginSuccess para que viva ya en el árbol de la app y
    // sobreviva al salto a Inicio (notistack NO reinicia el temporizador al cambiar de ruta).
    // 10 s en vez de `persist` a propósito: persistente ocuparía uno de los 3 cupos del
    // provider para siempre si nadie la cierra, y empujaría hacia abajo todos los toasts
    // siguientes. Con la X de cierre, quien quiera quitarla antes puede.
    yield put(
      uiSlice.actions.enqueueSnackbar(
        createSnack({
          key: 'login-welcome',
          variant: 'success',
          message: i18n.t('auth.welcomeBack', {
            ns: 'notifications',
            name: loggedUser.first_name || loggedUser.full_name || '',
            defaultValue: '¡Hola de nuevo! Qué bueno tenerte por acá.'
          }),
          options: { autoHideDuration: 10000, preventDuplicate: true }
        })
      )
    );

    if (isTrainer) {
      const userId = loggedUser.id;
      yield put(availabilitySlice.actions.getLatest(userId));
    }
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(authSlice.actions.loginError(message));
  }
}

/**
 * Self-registration saga
 *
 * @param {*} action
 */
function* selfRegister(action) {
  try {
    const response = yield auth.selfRegister(action.payload);
    const {
      data: { selfRegister: registerResponse }
    } = response;

    if (registerResponse.status === 'MUST_VERIFY_EMAIL') {
      // Store email BEFORE dispatching success so RegisterSuccess reads it on first render
      sessionStorage.setItem('pendingVerificationEmail', action.payload.email || '');
      yield put(authSlice.actions.registerSuccess());
      return;
    }

    // Fallback: if tokens are returned (no email verification required)
    if (registerResponse.tokens) {
      yield call(saveAuthInfo, registerResponse.tokens);
    }
    yield put(authSlice.actions.registerSuccess());

    const snack = createSnack({
      key: 'register-success',
      variant: 'success',
      message: i18n.t('auth.registerSuccess', { ns: 'common' })
    });
    yield put(uiSlice.actions.enqueueSnackbar(snack));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(authSlice.actions.registerError(message));
  }
}

/**
 * Logout saga
 *
 * @param {*} action
 */
function* logout() {
  try {
    // Se marca ANTES de vaciar el usuario: en cuanto el store queda sin sesión, `Authenticated`
    // dispara su efecto de redirección y necesita saber ya que esta salida fue voluntaria.
    yield call(markVoluntaryLogout);
    const response = yield auth.logout();
    const {
      data: { logout: logoutResponse }
    } = response;

    yield put(authSlice.actions.logoutSuccess(logoutResponse));
    yield call(removeAuthInfo);
    yield call(clearApolloCache);
    yield call(persistor.purge);
    // Reinicia el aviso de suscripción en gracia para que reaparezca tras el próximo login.
    sessionStorage.removeItem('otx_grace_alert_seen');
  } catch (ex) {
    // La salida falló y la sesión sigue viva: se limpia la marca para que no contamine una
    // caducidad posterior, donde el `?redirect=` sí es el comportamiento correcto.
    yield call(consumeVoluntaryLogout);
    const message = getUserFriendlyError(ex);
    yield put(authSlice.actions.logoutError(message));
  }
}

/**
 * Me saga
 *
 * @param {*} action
 */
function* me(action) {
  try {
    const response = yield user.me(action.payload);
    const {
      data: { me: currentUser }
    } = response;

    yield put(authSlice.actions.meSuccess(currentUser));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(authSlice.actions.meError(message));
  }
}

/**
 * Reset password saga
 *
 * @param {*} action
 */
function* resetPassword(action) {
  try {
    const { payload } = action;
    const {
      data: {
        updatePasswordWithToken: { updated, message }
      }
    } = yield auth.resetPassword(payload);

    const snack = createSnack({
      key: updated ? 'update-password-success' : 'update-password-error',
      variant: updated ? 'success' : 'error',
      message
    });

    if (updated) {
      yield put(authSlice.actions.resetPasswordSuccess());
    } else {
      yield put(authSlice.actions.resetPasswordError());
    }

    yield put(uiSlice.actions.enqueueSnackbar(snack));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(authSlice.actions.resetPasswordError(message));
  }
}

/**
 * Send reset token saga
 *
 * @param {*} action
 */
function* sendResetToken(action) {
  try {
    const { payload: email } = action;
    const {
      data: {
        sendPasswordResetToken: { sent, message }
      }
    } = yield auth.sendResetToken(email);

    const snack = createSnack({
      key: sent ? 'send-reset-token-success' : 'send-reset-token-error',
      variant: sent ? 'success' : 'error',
      message
    });

    if (sent) {
      yield put(authSlice.actions.sendResetTokenSuccess());
    } else {
      yield put(authSlice.actions.sendResetTokenError());
    }

    yield put(uiSlice.actions.enqueueSnackbar(snack));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(authSlice.actions.sendResetTokenError(message));
  }
}

/**
 * Verify email saga
 *
 * @param {*} action
 */
function* verifyEmail(action) {
  try {
    const { token, email } = action.payload;
    const response = yield auth.verifyEmail(token, email);
    const {
      data: { verifyEmail: verifyResponse }
    } = response;

    yield call(saveAuthInfo, { access_token: verifyResponse.access_token, refresh_token: null });
    yield put(settingsSlice.actions.find(verifyResponse.user.settings.id));
    yield take(settingsSlice.actions.findSuccess);
    yield put(
      authSlice.actions.loginSuccess({
        access_token: verifyResponse.access_token,
        refresh_token: null,
        expires_in: null,
        user: verifyResponse.user
      })
    );
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(authSlice.actions.verifyEmailError(message));
  }
}

/**
 * Update me saga
 *
 * @param {*} action
 */
function* updateMe(action) {
  try {
    const snack = createSnack({
      key: 'auth-update-me-notification-success',
      variant: 'success',
      message: i18n.t('me.update', { ns: 'notifications' })
    });

    const response = yield user.update(action.payload);
    const {
      data: { updateUser: updateUserResponse }
    } = response;

    yield put(authSlice.actions.updateMeSuccess(updateUserResponse));
    yield put(uiSlice.actions.enqueueSnackbar(snack));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(authSlice.actions.updateMeError(message));
  }
}

/**
 * Watch auth
 *
 * @export
 */
export default function* watchAuth() {
  yield takeLatest(authSlice.actions.generateResetToken, generateResetToken);
  yield takeLatest(authSlice.actions.login, login);
  yield takeLatest(authSlice.actions.register, selfRegister);
  yield takeLatest(authSlice.actions.logout, logout);
  yield takeLatest(authSlice.actions.me, me);
  yield takeLatest(authSlice.actions.resetPassword, resetPassword);
  yield takeLatest(authSlice.actions.sendResetToken, sendResetToken);
  yield takeLatest(authSlice.actions.verifyEmail, verifyEmail);
  yield takeLatest(authSlice.actions.updateMe, updateMe);
}
